import { Trans } from '@lingui/macro'
//import { TraceEvent } from '@uniswap/analytics'
import { InterfaceElementName } from '@uniswap/analytics-events'
import { formatPriceImpact } from '@uniswap/conedison/format'
import { Percent, TradeType } from '@uniswap/sdk-core'
import { useWeb3React } from '@web3-react/core'
import Column from 'components/Column'
import { MouseoverTooltip } from 'components/Tooltip'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import { ReactNode } from 'react'
import { AlertTriangle } from 'react-feather'
import { InterfaceTrade } from 'state/routing/types'
import styled, { useTheme } from 'styled-components/macro'
import { ThemedText } from 'theme'
import { formatCurrencyAmount } from 'utils/formatCurrencyAmount'
import { formatTransactionAmount, priceToPreciseFloat } from 'utils/formatNumbers'
//import { formatSwapButtonClickEventProperties } from 'utils/loggingFormatters'
import { getPriceImpactWarning } from 'utils/prices'

import { ButtonError, SmallButtonPrimary } from '../Button'
import Row, { AutoRow, RowBetween, RowFixed } from '../Row'
import { SwapCallbackError, SwapShowAcceptChanges } from './styleds'
import { Label } from './SwapModalHeaderAmount'

const DetailsContainer = styled(Column)`
  padding: 0 2px;
`

const StyledAlertTriangle = styled(AlertTriangle)`
  margin-right: 8px;
  min-width: 24px;
`

const ReceiverNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  margin-top: 4px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.accentWarning}55;
  background: ${({ theme }) => theme.accentWarning}11;
`

const ReceiverNoticeRow = styled(Row)`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`

const ReceiverAddressText = styled(ThemedText.Caption)`
  font-family: monospace;
  word-break: break-all;
  color: ${({ theme }) => theme.textPrimary};
`

const ReceiverNoticeTitle = styled(ThemedText.BodySmall)`
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
`

const ReceiverNoticeSub = styled(ThemedText.Caption)`
  color: ${({ theme }) => theme.textSecondary};
  line-height: 1.3;
`

const ReceiverNoticeError = styled(ThemedText.Caption)`
  color: ${({ theme }) => theme.accentWarning};
  line-height: 1.3;
`

const ConfirmButton = styled(ButtonError)`
  height: 56px;
  margin-top: 4px;
`

const DetailRowValue = styled(ThemedText.BodySmall)`
  text-align: right;
  overflow-wrap: break-word;
`

export default function SwapModalFooter({
  trade,
  allowedSlippage,
  hash: _hash,
  onConfirm,
  swapErrorMessage,
  disabledConfirm,
  swapQuoteReceivedDate: _swapQuoteReceivedDate,
  fiatValueInput: _fiatValueInput,
  fiatValueOutput: _fiatValueOutput,
  showAcceptChanges,
  onAcceptChanges,
  receiverAddress,
  receiverMatchesCalldata = true,
}: {
  trade: InterfaceTrade
  hash?: string
  allowedSlippage: Percent
  onConfirm: () => void
  swapErrorMessage?: ReactNode
  disabledConfirm: boolean
  swapQuoteReceivedDate?: Date
  fiatValueInput: { data?: number; isLoading: boolean }
  fiatValueOutput: { data?: number; isLoading: boolean }
  showAcceptChanges: boolean
  onAcceptChanges: () => void
  receiverAddress?: string
  receiverMatchesCalldata?: boolean
}) {
  void _hash
  void _swapQuoteReceivedDate
  void _fiatValueInput
  void _fiatValueOutput
  const theme = useTheme()
  const { chainId } = useWeb3React()
  const nativeCurrency = useNativeCurrency(chainId)

  const label = `${trade.executionPrice.baseCurrency?.symbol} `
  const labelInverted = `${trade.executionPrice.quoteCurrency?.symbol}`
  const formattedPrice = formatTransactionAmount(priceToPreciseFloat(trade.executionPrice))

  return (
    <>
      <DetailsContainer gap="sm">
        <ThemedText.BodySmall>
          <Row align="flex-start" justify="space-between" gap="sm">
            <Label fontSize="13px">
              <Trans>Exchange rate</Trans>
            </Label>
            <DetailRowValue fontSize="13px" color="textPrimary" fontWeight={500}>{`1 ${labelInverted} = ${
              formattedPrice ?? '-'
            } ${label}`}</DetailRowValue>
          </Row>
        </ThemedText.BodySmall>
        <ThemedText.BodySmall>
          <Row align="flex-start" justify="space-between" gap="sm">
            <MouseoverTooltip
              text={
                <Trans>
                  The fee paid to miners who process your transaction. This must be paid in ${nativeCurrency.symbol}.
                  Approximately amount!
                </Trans>
              }
            >
              <Label fontSize="13px" cursor="help">
                <Trans>Estimated gas fee</Trans>
              </Label>
            </MouseoverTooltip>
            <DetailRowValue fontSize="13px" color="textPrimary" fontWeight={500}>
              {trade.gasUseEstimateUSD ? `$${trade.gasUseEstimateUSD}` : '-'}
            </DetailRowValue>
          </Row>
        </ThemedText.BodySmall>
        <ThemedText.BodySmall>
          <Row align="flex-start" justify="space-between" gap="sm">
            <MouseoverTooltip text={<Trans>The impact your trade has on the market price of this pool.</Trans>}>
              <Label fontSize="13px" cursor="help">
                <Trans>Price impact</Trans>
              </Label>
            </MouseoverTooltip>
            <DetailRowValue fontSize="13px" fontWeight={500} color={getPriceImpactWarning(trade.priceImpact)}>
              {trade.priceImpact ? formatPriceImpact(trade.priceImpact) : '-'}
            </DetailRowValue>
          </Row>
        </ThemedText.BodySmall>
        <ThemedText.BodySmall>
          <Row align="flex-start" justify="space-between" gap="sm">
            <MouseoverTooltip
              text={
                trade.tradeType === TradeType.EXACT_INPUT ? (
                  <Trans>
                    The minimum amount you are guaranteed to receive. If the price slips any further, your transaction
                    will revert.
                  </Trans>
                ) : (
                  <Trans>
                    The maximum amount you are guaranteed to spend. If the price slips any further, your transaction
                    will revert.
                  </Trans>
                )
              }
            >
              <Label fontSize="13px" cursor="help">
                {trade.tradeType === TradeType.EXACT_INPUT ? (
                  <Trans>Minimum received</Trans>
                ) : (
                  <Trans>Maximum sent</Trans>
                )}
              </Label>
            </MouseoverTooltip>
            <DetailRowValue fontSize="13px" color="textPrimary" fontWeight={500}>
              {trade.tradeType === TradeType.EXACT_INPUT
                ? `${formatCurrencyAmount(trade.minimumAmountOut(allowedSlippage), 6)} ${
                    trade.outputAmount.currency.symbol
                  }`
                : `${formatCurrencyAmount(trade.maximumAmountIn(allowedSlippage), 6)} ${
                    trade.inputAmount.currency.symbol
                  }`}
            </DetailRowValue>
          </Row>
        </ThemedText.BodySmall>
      </DetailsContainer>
      {receiverAddress && !showAcceptChanges && (
        <ReceiverNotice>
          <ReceiverNoticeRow>
            <StyledAlertTriangle size={18} />
            <Column gap="xs">
              <ReceiverNoticeTitle>
                <Trans>Receiver address enabled!</Trans>
              </ReceiverNoticeTitle>
              <ReceiverNoticeSub>
                <Trans>Tokens will be sent to the address below instead of your connected wallet.</Trans>
              </ReceiverNoticeSub>
              <ReceiverAddressText>{receiverAddress}</ReceiverAddressText>
              {!receiverMatchesCalldata && (
                <ReceiverNoticeError>
                  <Trans>
                    Warning: Current transaction data does not include this receiver. Please refresh the quote or try
                    again.
                  </Trans>
                </ReceiverNoticeError>
              )}
            </Column>
          </ReceiverNoticeRow>
        </ReceiverNotice>
      )}
      {showAcceptChanges ? (
        <SwapShowAcceptChanges data-testid="show-accept-changes">
          <RowBetween>
            <RowFixed>
              <StyledAlertTriangle size={20} />
              <ThemedText.DeprecatedMain color={theme.accentAction}>
                <Trans>Price updated</Trans>
              </ThemedText.DeprecatedMain>
            </RowFixed>
            <SmallButtonPrimary onClick={onAcceptChanges}>
              <Trans>Accept</Trans>
            </SmallButtonPrimary>
          </RowBetween>
        </SwapShowAcceptChanges>
      ) : (
        <AutoRow>
          {/*<TraceEvent
            events={[BrowserEvent.onClick]}
            element={InterfaceElementName.CONFIRM_SWAP_BUTTON}
            name={SwapEventName.SWAP_SUBMITTED_BUTTON_CLICKED}
            properties={formatSwapButtonClickEventProperties({
              trade,
              hash,
              allowedSlippage,
              transactionDeadlineSecondsSinceEpoch,
              isAutoSlippage,
              isAutoRouterApi: routerPreference === RouterPreference.AUTO || routerPreference === RouterPreference.API,
              swapQuoteReceivedDate,
              routes,
              fiatValueInput: fiatValueInput.data,
              fiatValueOutput: fiatValueOutput.data,
            })}
            </TraceEvent>
          >*/}
          <ConfirmButton
            data-testid="confirm-swap-button"
            onClick={onConfirm}
            disabled={disabledConfirm || (receiverAddress ? !receiverMatchesCalldata : false)}
            $borderRadius="6px"
            id={InterfaceElementName.CONFIRM_SWAP_BUTTON}
          >
            <ThemedText.HeadlineSmall color="accentTextLightPrimary">
              <Trans>Confirm swap</Trans>
            </ThemedText.HeadlineSmall>
          </ConfirmButton>

          {swapErrorMessage ? <SwapCallbackError error={swapErrorMessage} /> : null}
        </AutoRow>
      )}
    </>
  )
}
